import React from 'react';
import { format } from 'date-fns';

/**
 * Helpers for rendering an indent's `desired_po_date` field on PM-facing
 * views (Indent Register pending tab, PO consolidation dialog,
 * Edit Indent dialog). Urgency thresholds mirror
 * `getDeliveryDateColor` in SOFulfillmentPage.js exactly so the PM sees
 * the same colour grammar across SO Fulfillment and Indent Register.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Whole-day diff between today (local midnight) and the given date.
 * Positive = future, negative = overdue (past), 0 = today.
 * Returns null for null / missing / unparseable input.
 */
export function daysUntilDesiredPo(iso) {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  // Normalise both to local midnight so the diff is whole days
  // regardless of the time component on the stored ISO string.
  const targetMidnight = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((targetMidnight - todayMidnight) / MS_PER_DAY);
}

/**
 * Tailwind class string for the date cell background+text. Mirrors
 * getDeliveryDateColor in SOFulfillmentPage.js:271-296:
 *   daysUntil <  3   -> red    (includes overdue)
 *   daysUntil 3..7   -> amber
 *   daysUntil >  7   -> green
 *   daysUntil null   -> neutral (no date / unparseable)
 */
export function getDesiredPoDateColor(daysUntil) {
  if (daysUntil === null || daysUntil === undefined) {
    return 'text-gray-400';
  }
  if (daysUntil < 3) {
    return 'text-red-600 bg-red-50 font-medium';
  }
  if (daysUntil <= 7) {
    return 'text-amber-600 bg-amber-50';
  }
  return 'text-green-600 bg-green-50';
}

/**
 * Format the date in the page's existing dd/MM/yyyy style (matches
 * the other date renders in IndentRegisterPage). Returns the em-dash
 * for null / missing / unparseable input — callers should suppress
 * the days-overdue / days-left sub-label in that case.
 */
export function formatDesiredPoDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'dd/MM/yyyy');
}

/**
 * Combined date cell + days-overdue/left sub-label. Used in both the
 * Indent Register Pending tab table and the PO consolidation dialog.
 *
 *   - Real date: coloured pill + sub-label ("Xd overdue" / "Xd left",
 *                "today" when daysUntil === 0).
 *   - Null / unparseable: '—' in neutral colour, no sub-label.
 */
export function DesiredPoDateCell({ value }) {
  const days = daysUntilDesiredPo(value);
  const colorClass = getDesiredPoDateColor(days);
  const dateText = formatDesiredPoDate(value);

  // Null / unparseable: em-dash only, no sub-label, no urgency colour.
  if (days === null) {
    return (
      <div className="text-center">
        <div className="text-xs text-gray-400">{dateText}</div>
      </div>
    );
  }

  let subLabel;
  let subClass;
  if (days < 0) {
    subLabel = `${Math.abs(days)}d overdue`;
    subClass = 'text-red-500 font-medium';
  } else if (days === 0) {
    subLabel = 'today';
    subClass = 'text-red-500 font-medium';
  } else {
    subLabel = `${days}d left`;
    subClass = days <= 3 ? 'text-amber-600' : 'text-gray-400';
  }

  return (
    <div className="text-center">
      <div className={`text-xs px-1 py-0.5 rounded inline-block ${colorClass}`}>
        {dateText}
      </div>
      <div className={`text-xs mt-0.5 ${subClass}`}>{subLabel}</div>
    </div>
  );
}

export default DesiredPoDateCell;
